import { __awaiter } from "tslib";
import { LexiWallet } from "@civic/lexi";
import jsonPath from "jsonpath";
import { clone, mergeDeepLeft } from "ramda";
import { hash } from "tweetnacl";
import storageAdapterFactory from "../factory/storageAdapterFactory";
import { didServiceEndpoint } from "../lib/did";
import buildNewDocumentFromJsonPathNodes from "../utils/buildNewDocumentFromJsonPathNodes";
class CivicMeAdapter {
    constructor(did, resolve, adapterSettings, jsonPaths, globalEncryptionSettings = null) {
        this.did = did;
        this.resolve = resolve;
        this.adapterSettings = adapterSettings;
        if (globalEncryptionSettings !== null) {
            this.lexiWallet = new LexiWallet({
                signMessage: globalEncryptionSettings.signMessage,
            }, globalEncryptionSettings.did, {
                resolve: (innerDid) => __awaiter(this, void 0, void 0, function* () {
                    const didDocument = yield this.resolve(innerDid);
                    return {
                        didDocument,
                        didDocumentMetadata: {},
                        didResolutionMetadata: {},
                    };
                }),
                publicSigningString: globalEncryptionSettings.publicSigningString
                    ? Buffer.from(hash(Buffer.from(globalEncryptionSettings.publicSigningString))).toString("base64")
                    : undefined,
            });
        }
        else {
            this.lexiWallet = null;
        }
        this.keyedStorageAdapters = this.setupKeyedStorageAdapters(adapterSettings);
        this.jsonPathStorageAdapter = this.setupJsonPathStorageAdapter(jsonPaths);
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(Object.values(this.keyedStorageAdapters).map((sa) => sa.init()));
        });
    }
    has() {
        return __awaiter(this, void 0, void 0, function* () {
            const didDocument = yield this.resolveDidDocument();
            const allPromises = Object.entries(this.keyedStorageAdapters).map(([key, adapter]) => {
                const didService = this.didAttached(key);
                const serviceEndpoint = didServiceEndpoint(didDocument.service, key);
                // the configuration specifies a DID entry but no service entry endpoint exists
                if (didService && serviceEndpoint === null) {
                    return false;
                }
                // if the item is located on the DID then map the key to the content in the service endpoint,
                // otherwise just use the key
                const resolvedKey = serviceEndpoint && didService !== false
                    ? serviceEndpoint.serviceEndpoint.slice(didService.urlPrefix.length)
                    : key;
                return adapter.has(resolvedKey);
            });
            const allResults = yield Promise.all(allPromises);
            const itemsWithContent = allResults.filter((item) => item);
            return itemsWithContent.length > 0;
        });
    }
    set(_contentPath, currentDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            // we call this first because if we have multiple adapters that use encryption and we don't have a cached key, they add a request for signing to the event loop because of async, if the key already exists this is basically a nop
            if (this.lexiWallet) {
                yield this.lexiWallet.generateKeyForSigning();
            }
            // TODO validate currentDocument
            // sort paths by the length of the elements in the path, the long it is the more detailed it will be
            // we than sort by the longest first, so we can extract those elements first.
            const sortedJsonPaths = Object.keys(this.jsonPathStorageAdapter)
                .map((path) => jsonPath.parse(path))
                .sort((a, b) => b.length - a.length)
                .map((paths) => jsonPath.stringify(paths));
            // split the large document into smaller documents we can send to each adapter,
            // adapterUpdates stores those fractional documents against the adapter key.
            const adapterUpdates = {};
            // clone the document first because we will make changes to the document we don't want to keep.
            let documentToSplit = clone(currentDocument);
            // start with the longest JSON paths, create smaller fractional documents from the larger document.
            // To avoid saving the whole document when a specific path is specified (e.g $.privateRemoteProfile)
            (sortedJsonPaths.includes(_contentPath)
                ? [_contentPath]
                : sortedJsonPaths).forEach((path) => {
                const adapter = this.jsonPathStorageAdapter[path];
                const key = this.getKeyFromAdapter(adapter);
                // we do this, so we make sure the fractional documents we always work with are objects, not primitive types.
                // ex. {email: "test@test.com" } instead for test@test.com
                const data = jsonPath.nodes(documentToSplit, path);
                if (data.length === 0) {
                    return;
                }
                const value = buildNewDocumentFromJsonPathNodes(data);
                // if we already have data for an adapter, we concat with the data we already have.
                if (!adapterUpdates[key]) {
                    adapterUpdates[key] = value;
                }
                else {
                    adapterUpdates[key] = mergeDeepLeft(adapterUpdates[key], value);
                }
                // remove the part we just copied from the cloned mutable document.
                if (path.split(".").length > 1) {
                    // REFACTOR maybe find a cleaner way of doing this
                    jsonPath.value(documentToSplit, path, undefined);
                    documentToSplit = JSON.parse(JSON.stringify(documentToSplit));
                }
                else {
                    documentToSplit = {};
                }
            });
            // call the adapters' set methods, collect the content ids they return and map them against the keys of the adapters
            const contentIdMap = (yield Promise.all(Object.keys(adapterUpdates).map((key) => __awaiter(this, void 0, void 0, function* () {
                const receiptFromAdapter = yield this.keyedStorageAdapters[key].set(key, adapterUpdates[key]);
                return {
                    [key]: {
                        reference: receiptFromAdapter,
                        didAware: this.didAttached(key) || undefined,
                    },
                };
            })))).reduce((accumulator, value) => (Object.assign(Object.assign({}, accumulator), value)));
            return { results: contentIdMap };
        });
    }
    /**
     * Retrieve the content from the all child adapters and combine into a single document
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    get(_contentId) {
        return __awaiter(this, void 0, void 0, function* () {
            const getForChildAdapter = this.getForChildAdapter.bind(this);
            if (!(yield this.has())) {
                return null;
            }
            // Iterate through all child adapters, and retrieve their contents
            const allPromises = Object.entries(this.keyedStorageAdapters).map(([key, adapter]) => getForChildAdapter(key, adapter));
            // merge the results (removing any null results)
            const allResults = yield Promise.all(allPromises);
            const successfullResults = allResults.filter(Boolean);
            let results = {};
            successfullResults.forEach((result) => {
                results = mergeDeepLeft(results, result);
            });
            return results;
        });
    }
    getWrappedAdapters() {
        return Object.values(this.jsonPathStorageAdapter);
    }
    /**
     * Given a storage adapter, lookup the service endpoint appropriate for that adapter from the did document
     * and then retrieve the content from the service endpoint.
     * @param key
     * @param adapter
     * @private
     */
    getForChildAdapter(key, adapter) {
        return __awaiter(this, void 0, void 0, function* () {
            const didDocument = yield this.resolveDidDocument();
            const didService = this.didAttached(key);
            const serviceEndpoint = didServiceEndpoint(didDocument.service, key);
            if (didService && serviceEndpoint === null) {
                return null;
            }
            // if the item is located on the DID then map the key to the content in the service endpoint,
            // otherwise just use the key
            const resolvedKey = serviceEndpoint && didService !== false
                ? serviceEndpoint.serviceEndpoint.slice(didService.urlPrefix.length)
                : key;
            try {
                return adapter.get(resolvedKey);
            }
            catch (e) {
                return null;
            }
        });
    }
    adaptersByKey(key) {
        return Object.keys(this.keyedStorageAdapters)
            .filter((loopKey) => loopKey === key)
            .map((loopKey) => this.adapterSettings[loopKey]);
    }
    didAttached(key) {
        var _a, _b;
        return (_b = (_a = this.adaptersByKey(key).pop()) === null || _a === void 0 ? void 0 : _a.didAware) !== null && _b !== void 0 ? _b : false;
    }
    getKeyFromAdapter(adapter) {
        return Object.keys(this.keyedStorageAdapters).filter((key) => this.keyedStorageAdapters[key] === adapter)[0];
    }
    setupKeyedStorageAdapters(adapterMetadata) {
        return Object.keys(adapterMetadata)
            .map((adapterKey) => {
            const adapter = storageAdapterFactory(this.adapterSettings[adapterKey], this.lexiWallet);
            return {
                [adapterKey]: adapter,
            };
        })
            .reduce((accumulator, newValue) => (Object.assign(Object.assign({}, accumulator), newValue)));
    }
    setupJsonPathStorageAdapter(jsonPaths) {
        return Object.keys(jsonPaths)
            .map((jPath) => {
            return {
                [jPath]: this.keyedStorageAdapters[jsonPaths[jPath]],
            };
        })
            .reduce((accumulator, newValue) => (Object.assign(Object.assign({}, accumulator), newValue)));
    }
    resolveDidDocument() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolve(this.did);
        });
    }
}
export default CivicMeAdapter;
